// Which lines a drawn symbol picks up, and which symbols a drawn line's ends pick up.
//
// The parse leaves a line ending a short gap outside the symbol it touches, about 11 px on the
// human-annotated drawings (p95 12 px), and a box drawn by hand sits a few percent off the object.
// So a symbol reaches LINE_ATTACH_CATCH_PX plus REACH_SHARE of its shorter side (at most
// REACH_SHARE_MAX) from its outline, inside or out: a line meets a vessel at its wall, and a box
// drawn round a whole package unit must not take every loose end inside it. Whatever a line is
// linked to (a symbol, or the line it tees onto) holds the line end nearest to it, and a linked
// symbol is not a candidate for the line's other ends; a free end goes to the nearest symbol in
// reach, then the smallest. The parse marks where a line meets a vessel wall with a junction (a
// Signal Tee) and links the line to it, so a junction on a drawn symbol's outline gets a short
// line to the symbol. Pure: the editor passes its current nodes and edges.

import { parseLineStringSegments, type Edge, type Node } from '@/service/graph'
import { nearestPointOnPath, type Pt } from './shapeSnap'

export const LINE_ATTACH_CATCH_PX = 16
const REACH_SHARE = 0.1
const REACH_SHARE_MAX = 64
/** Piece ends this close together are one joint of a line stored in pieces, not two ends. */
const JOIN_PX = 1

interface Shape {
  id: string
  x0: number
  y0: number
  x1: number
  y1: number
  vertices?: Pt[]
  reach: number
}

const isAnnotation = (node: Node): boolean => node.pnid_level_i?.trim().toLowerCase() === 'annotation'

const isJunction = (node: Node): boolean =>
  node.type === 'symbol' &&
  (node.name === 'junction' || node.name === 'Signal Tee' || node.pnid_level_iii === 'Signal Tee')

function shapeOf(node: Node, catchPx: number): Shape | null {
  if (node.type !== 'symbol' || isAnnotation(node)) return null
  const { x, y, width, height } = node
  if (![x, y, width, height].every(Number.isFinite)) return null
  const vertices = node.vertices && node.vertices.length >= 3 ? node.vertices : undefined
  const reach = catchPx + Math.min(REACH_SHARE_MAX, REACH_SHARE * Math.min(width!, height!))
  return { id: node.id as string, x0: x!, y0: y!, x1: x! + width!, y1: y! + height!, vertices, reach }
}

/** Distance from `p` to the shape's outline, from inside or out. */
function distanceTo(p: Pt, s: Pick<Shape, 'x0' | 'y0' | 'x1' | 'y1' | 'vertices'>): number {
  if (s.vertices) return nearestPointOnPath(p, s.vertices, true).dist
  const outside = Math.hypot(Math.max(s.x0 - p.x, 0, p.x - s.x1), Math.max(s.y0 - p.y, 0, p.y - s.y1))
  return outside > 0 ? outside : Math.min(p.x - s.x0, s.x1 - p.x, p.y - s.y0, s.y1 - p.y)
}

/** Distance from `p` to whatever a line is linked to: a line's path, or any other node's outline. */
function distanceToNode(p: Pt, node: Node): number {
  if (node.type === 'connection') {
    const paths = node.geometry_wkt ? parseLineStringSegments(node.geometry_wkt) : []
    return Math.min(
      Infinity,
      ...paths.map((seg) => nearestPointOnPath(p, seg.map(([x, y]) => ({ x, y })), false).dist),
    )
  }
  const { x, y, width, height } = node
  if (![x, y, width, height].every(Number.isFinite)) return Infinity
  const vertices = node.vertices && node.vertices.length >= 3 ? node.vertices : undefined
  return distanceTo(p, { x0: x!, y0: y!, x1: x! + width!, y1: y! + height!, vertices })
}

/** A line's ends. A MULTILINESTRING has two per piece, less the joints where pieces meet. */
function lineEnds(node: Node): Pt[] {
  if (node.type !== 'connection' || isAnnotation(node) || !node.geometry_wkt) return []
  const ends = parseLineStringSegments(node.geometry_wkt).flatMap((seg, piece) => [
    { piece, x: seg[0][0], y: seg[0][1] },
    { piece, x: seg[seg.length - 1][0], y: seg[seg.length - 1][1] },
  ])
  return ends
    .filter((a) => !ends.some((b) => b.piece !== a.piece && Math.hypot(a.x - b.x, a.y - b.y) <= JOIN_PX))
    .map(({ x, y }) => ({ x, y }))
}

interface Scene {
  nodes: ReadonlyMap<string, Node>
  shapes: Shape[]
  shapeById: Map<string, Shape>
  /** line id → the symbols and lines linked to it, by any relation. */
  linked: Map<string, Set<string>>
}

function sceneOf(nodes: ReadonlyMap<string, Node>, edges: ReadonlyMap<string, Edge>, catchPx: number): Scene {
  const shapes = [...nodes.values()].map((n) => shapeOf(n, catchPx)).filter((s): s is Shape => s !== null)
  const linked = new Map<string, Set<string>>()
  for (const e of edges.values()) {
    for (const [line, other] of [
      [e.fromNodeId, e.toNodeId],
      [e.toNodeId, e.fromNodeId],
    ]) {
      // A tag labels a line along its length; only what the line meets at an end holds one.
      const kind = nodes.get(other)?.type
      if (nodes.get(line)?.type !== 'connection' || (kind !== 'symbol' && kind !== 'connection') || line === other) continue
      const set = linked.get(line) ?? new Set<string>()
      set.add(other)
      linked.set(line, set)
    }
  }
  return { nodes, shapes, shapeById: new Map(shapes.map((s) => [s.id, s])), linked }
}

/** Each free end of a line with the symbol it is at, or null when no symbol is in reach. */
function freeEnds(line: Node, scene: Scene): Array<{ end: Pt; symbol: string | null }> {
  const ends = lineEnds(line)
  const linked = scene.linked.get(line.id as string) ?? new Set<string>()
  const held = new Set<number>()
  for (const id of linked) {
    const node = scene.nodes.get(id)
    if (!node || ends.length === 0) continue
    const dist = ends.map((p) => distanceToNode(p, node))
    if (!dist.some(Number.isFinite)) continue
    held.add(dist.indexOf(Math.min(...dist)))
  }
  return ends
    .filter((_, i) => !held.has(i))
    .map((end) => {
      let best: { id: string; dist: number; area: number } | null = null
      for (const s of scene.shapes) {
        if (linked.has(s.id)) continue
        const dist = distanceTo(end, s)
        if (dist > s.reach) continue
        const area = (s.x1 - s.x0) * (s.y1 - s.y0)
        if (
          !best ||
          dist < best.dist ||
          (dist === best.dist && (area < best.area || (area === best.area && s.id < best.id)))
        ) {
          best = { id: s.id, dist, area }
        }
      }
      return { end, symbol: best?.id ?? null }
    })
}

/** Lines with a free end at the symbol that are not linked to it yet. */
export function linesEndingAtSymbol(
  symbolId: string,
  nodes: ReadonlyMap<string, Node>,
  edges: ReadonlyMap<string, Edge>,
  catchPx = LINE_ATTACH_CATCH_PX,
): string[] {
  const scene = sceneOf(nodes, edges, catchPx)
  const symbol = scene.shapeById.get(symbolId)
  if (!symbol) return []
  const out: string[] = []
  for (const node of nodes.values()) {
    if (node.type !== 'connection' || scene.linked.get(node.id as string)?.has(symbolId)) continue
    // Cheap reject before ranking every symbol against the line's ends.
    if (!lineEnds(node).some((p) => distanceTo(p, symbol) <= symbol.reach)) continue
    if (freeEnds(node, scene).some((e) => e.symbol === symbolId)) out.push(node.id as string)
  }
  return out
}

/** Symbols at the line's free ends that are not linked to it yet. */
export function symbolsAtLineEnds(
  lineId: string,
  nodes: ReadonlyMap<string, Node>,
  edges: ReadonlyMap<string, Edge>,
  catchPx = LINE_ATTACH_CATCH_PX,
): string[] {
  const line = nodes.get(lineId)
  if (!line) return []
  const symbols = freeEnds(line, sceneOf(nodes, edges, catchPx))
    .map((e) => e.symbol)
    .filter((s): s is string => s !== null)
  return [...new Set(symbols)]
}

/** A short line from a junction on a symbol's outline to the symbol. */
export interface JunctionConnector {
  junction: string
  /** LINESTRING from the junction's centre to the nearest point of the symbol's outline. */
  geometryWkt: string
  /** The line whose class the connector takes. */
  classFrom: string
}

/**
 * The connectors joining a symbol to the junctions on its outline. The parse marks where a line
 * meets a vessel wall with a junction and links the line to it, so the line has no end left for
 * the vessel; a connector from that junction to the vessel joins them and leaves every parse link
 * as it is. A junction whose only lines lie along the outline (the parse reads a vessel wall as a
 * pipe), or that already shares a line with the symbol, gets none.
 */
export function junctionConnectors(
  symbolId: string,
  nodes: ReadonlyMap<string, Node>,
  edges: ReadonlyMap<string, Edge>,
  catchPx = LINE_ATTACH_CATCH_PX,
): JunctionConnector[] {
  const scene = sceneOf(nodes, edges, catchPx)
  const symbol = scene.shapeById.get(symbolId)
  if (!symbol) return []
  const out: JunctionConnector[] = []
  for (const s of scene.shapes) {
    const junction = nodes.get(s.id)
    if (s.id === symbolId || !junction || !isJunction(junction)) continue
    const centre = { x: (s.x0 + s.x1) / 2, y: (s.y0 + s.y1) / 2 }
    if (distanceTo(centre, symbol) > symbol.reach) continue
    const lines = [...scene.linked].filter(([, linked]) => linked.has(s.id)).map(([id]) => id)
    if (lines.some((id) => scene.linked.get(id)?.has(symbolId))) continue
    const meeting = lines.filter((id) => {
      const ends = lineEnds(nodes.get(id)!)
      if (ends.length === 0) return false
      // The widest pair of ends, not the spread from the first: a line stored in pieces can
      // list a middle piece first.
      const span = Math.max(...ends.flatMap((p) => ends.map((q) => Math.hypot(p.x - q.x, p.y - q.y))))
      const alongOutline = ends.every((p) => distanceTo(p, symbol) <= symbol.reach) && span > 2 * symbol.reach
      return !alongOutline
    })
    if (meeting.length === 0) continue
    const reachAway = (id: string): number =>
      Math.max(...lineEnds(nodes.get(id)!).map((p) => distanceTo(p, symbol)))
    const to = nearestOnOutline(centre, symbol)
    out.push({
      junction: s.id,
      geometryWkt: `LINESTRING (${centre.x} ${centre.y}, ${to.x} ${to.y})`,
      // The nozzle line runs away from the symbol; a short wall piece the parse cut stays at it.
      classFrom: meeting.sort((a, b) => reachAway(b) - reachAway(a) || a.localeCompare(b))[0],
    })
  }
  return out
}

function nearestOnOutline(p: Pt, s: Shape): Pt {
  if (s.vertices) return nearestPointOnPath(p, s.vertices, true).point
  const inside = p.x >= s.x0 && p.x <= s.x1 && p.y >= s.y0 && p.y <= s.y1
  if (!inside) return { x: Math.min(Math.max(p.x, s.x0), s.x1), y: Math.min(Math.max(p.y, s.y0), s.y1) }
  const sides: Array<[number, Pt]> = [
    [p.x - s.x0, { x: s.x0, y: p.y }],
    [s.x1 - p.x, { x: s.x1, y: p.y }],
    [p.y - s.y0, { x: p.x, y: s.y0 }],
    [s.y1 - p.y, { x: p.x, y: s.y1 }],
  ]
  return sides.reduce((best, side) => (side[0] < best[0] ? side : best))[1]
}
